import { defineComponent, toRefs, PropType, computed, ref, nextTick, watch } from '@vue/composition-api';
import {
  AdgroupFormData,
  CampaignFormData,
  FullNormalFormData,
  AcoFormData,
  ACTION_TYPE,
  NormalCreative,
} from '@tt4b/ttam-enums';
import { useCreativeWebsiteUrlValidate, useStdCreativeAdgroupIs, useCreativeActionTypeIs } from '@tt4b/ttam-hooks';
import { useInitRecommUrl } from '../hooks';
import { usePixelDomainProps } from './../../tracking/hooks/usePixel';

import CreativeWebsiteUrl from '../index';

const name = 'ttam-creative-website-url-compose';
export default defineComponent({
  name,
  components: {
    TtamCreativeWebsiteUrlInput: CreativeWebsiteUrl.TtamCreativeWebsiteUrlInput,
    TtamCreativeWebsiteUrlCheckUrl: CreativeWebsiteUrl.TtamCreativeWebsiteUrlCheckUrl,
    TtamCreativeWebsiteUrlRejectReason: CreativeWebsiteUrl.TtamCreativeWebsiteUrlRejectReason,
  },
  props: {
    campaignData: {
      type: Object as PropType<CampaignFormData>,
      default: () => ({}),
    },
    adgroupData: {
      type: Object as PropType<AdgroupFormData>,
      default: () => ({}),
    },
    creativeData: {
      type: Object as PropType<FullNormalFormData & AcoFormData>,
      default: () => ({}),
    },
    normalCreativeData: {
      type: Object as PropType<NormalCreative>,
      default: () => ({}),
    },
    activeSplitTestIndex: {
      type: Number,
      default: () => 0,
    },
    createMode: {
      type: String,
      default: () => ACTION_TYPE.CREATE,
    },
    shouldAttachmentCreativeWithLink: {
      type: Boolean,
      default: false,
    },
    rejectReason: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    isDisabledCreative: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const {
      adgroupData,
      campaignData,
      creativeData,
      createMode,
      normalCreativeData,
      isDisabledCreative,
      activeSplitTestIndex,
    } = toRefs(props);

    // 获取落地页的domain
    const { initExternalUrlDomain, isPcmPixel } = usePixelDomainProps({
      adgroupData,
      creativeData,
      createMode,
    });

    const { externalUrlValidator } = useCreativeWebsiteUrlValidate({
      campaignFormData: campaignData,
    });

    const { isChecking, computedURLSuggtionList, checkExternalUrl } = CreativeWebsiteUrl.useCheckUrl({
      createMode,
      campaignData,
      adgroupData,
      normalCreativeData,
      activeSplitTestIndex,
      creativeData,
    });

    const refExternalUrl = ref();

    const { data: recommendUrls } = useInitRecommUrl({ adgroupData });

    const changeExternalUrl = async (url = ''): Promise<void> => {
      checkExternalUrl(url);
      const { externalUrlDomain, domain } = await initExternalUrlDomain(url, true);
      emit('external-url-domain-change', externalUrlDomain);
      emit('domain-change', domain);
    };

    const urlChange = ref(false);

    const _externalUrl = computed({
      get() {
        return creativeData.value?.externalUrl || '';
      },
      set(newV: string) {
        emit('change', newV.trim());

        if (newV !== creativeData.value?.externalUrl) {
          urlChange.value = true;
        }

        nextTick(() => {
          refExternalUrl.value?.validate?.('');

          // checkUrl
          changeExternalUrl(newV);
        });
      },
    });

    watch(isPcmPixel, async val => {
      if (val) {
        const { externalUrlDomain, domain } = await initExternalUrlDomain(_externalUrl.value);
        emit('external-url-domain-change', externalUrlDomain);
        emit('domain-change', domain);
      }
    });

    const { isEdit } = useCreativeActionTypeIs({
      createMode,
      adgroupFormData: adgroupData,
    });

    const { disabled: inputDisabled } = CreativeWebsiteUrl.useInputPropsDisabled({
      adgroupData,
      campaignData,
      createMode,
      isDisabledCreative,
    });

    const { isAco, isBrandAuction } = useStdCreativeAdgroupIs({
      adgroupFormData: adgroupData,
      campaignFormData: campaignData,
    });

    // 编辑时，如果修改URL则展示提示信息
    const tipsVisible = computed(() => isEdit.value && urlChange.value);

    // 是否展示落地页文本框
    const inputVisible = computed(() => !isBrandAuction.value || creativeData.value?.enableUrl);
    return {
      name,
      externalUrlValidator,
      _externalUrl,
      recommendUrls,
      computedURLSuggtionList,
      inputDisabled,
      inputVisible,

      refExternalUrl,

      isChecking,
      changeExternalUrl,
      tipsVisible,
      isAco,
    };
  },
});
